import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import RecurrenceEditor from "@/components/tasks/RecurrenceEditor";

function toLocalDatetimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(localVal) {
  if (!localVal) return null;
  return new Date(localVal).toISOString();
}

/**
 * Phase 21 — Task create/edit dialog.
 * Shared by the single-task quick create and the bulk-assign wizard.
 */
export default function TaskDialog({
  open, onOpenChange, initial = null, workerId, workerOptions = [],
  worksiteOptions = [], allowRecurrence = true, onSaved, onSubmitOverride,
}) {
  const isEdit = !!initial?.id;
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 30, 0, 0);
    return toLocalDatetimeInput(d.toISOString());
  }, []);
  const defaultEnd = useMemo(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 90, 0, 0);
    return toLocalDatetimeInput(d.toISOString());
  }, []);

  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [worker, setWorker] = useState(initial?.worker_id || workerId || "");
  const [worksite, setWorksite] = useState(initial?.worksite_id || "");
  const [start, setStart] = useState(toLocalDatetimeInput(initial?.scheduled_start) || defaultStart);
  const [end, setEnd] = useState(toLocalDatetimeInput(initial?.scheduled_end) || defaultEnd);
  const [budget, setBudget] = useState(initial?.budget_minutes || 60);
  const [rrule, setRrule] = useState(initial?.recurrence_rrule || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setDescription(initial?.description || "");
    setWorker(initial?.worker_id || workerId || "");
    setWorksite(initial?.worksite_id || "");
    setStart(toLocalDatetimeInput(initial?.scheduled_start) || defaultStart);
    setEnd(toLocalDatetimeInput(initial?.scheduled_end) || defaultEnd);
    setBudget(initial?.budget_minutes || 60);
    setRrule(initial?.recurrence_rrule || null);
  }, [open, initial?.id, workerId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title,
        description: description || null,
        scheduled_start: toIso(start),
        scheduled_end: toIso(end),
        budget_minutes: Number(budget),
        worksite_id: worksite || null,
      };
      if (allowRecurrence) body.recurrence_rrule = rrule || null;
      if (onSubmitOverride) {
        // Bulk-assign wizard path — capture the values, don't POST.
        await onSubmitOverride(body);
        onOpenChange(false);
        return;
      }
      if (isEdit) {
        await api.patch(`/tenant/tasks/${initial.id}`, body);
        toast.success("Task updated");
      } else {
        body.worker_id = worker;
        await api.post("/tenant/tasks", body);
        toast.success("Task created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit task" : "Create task"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update the schedule or budget." : "Assign a scheduled task to a worker."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              data-testid="task-title-input"
            />
          </div>
          <div>
            <Label htmlFor="task-desc">Description (optional)</Label>
            <Textarea
              id="task-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={2000}
              data-testid="task-desc-input"
            />
          </div>
          {!isEdit && (
            <div>
              <Label>Worker</Label>
              <Select value={worker} onValueChange={setWorker}>
                <SelectTrigger data-testid="task-worker-select">
                  <SelectValue placeholder="Choose a worker…" />
                </SelectTrigger>
                <SelectContent>
                  {workerOptions.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name || u.email} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Worksite (optional)</Label>
            <Select value={worksite || "__none__"} onValueChange={(v) => setWorksite(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="task-worksite-select">
                <SelectValue placeholder="No worksite" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No worksite</SelectItem>
                {worksiteOptions.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name} ({w.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="task-start">Start</Label>
              <Input
                id="task-start" type="datetime-local"
                value={start} onChange={(e) => setStart(e.target.value)}
                data-testid="task-start-input"
              />
            </div>
            <div>
              <Label htmlFor="task-end">End</Label>
              <Input
                id="task-end" type="datetime-local"
                value={end} onChange={(e) => setEnd(e.target.value)}
                data-testid="task-end-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="task-budget">Budget (minutes · 5–1440)</Label>
            <Input
              id="task-budget" type="number" min={5} max={1440} step={5}
              value={budget} onChange={(e) => setBudget(e.target.value)}
              data-testid="task-budget-input"
            />
          </div>
          {allowRecurrence && (
            <RecurrenceEditor value={rrule} onChange={setRrule} />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="task-cancel-btn">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || !title || !start || !end || (!isEdit && !workerId && !worker && !onSubmitOverride)}
            data-testid="task-save-btn"
          >
            {saving ? "Saving…" : isEdit ? "Update task" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
